import { batch } from 'react-redux';
import template from 'url-template';

import { get, isNil } from 'lodash';

import {
    DEVICE_ADD,
    DEVICE_REORDER,
    DEVICE_REMOVE,
    DEVICE_TOGGLE_SELECT,
    DEVICE_UPDATE,
    DEVICES_TOGGLE,
    FETCH_DEVICES,
    FETCH_DEVICE_OBJECTS,
    SET_DEVICE,
    SET_DEVICES_LOADING,
    SET_DEVICE_PROCESS_IDS,
    SET_DEVICE_TOTAL
} from '../actions/actionTypes';

import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';

import { resetLoadProgress, setLoadProgress, toggleLoadProgressBar } from './uiActions';

import { setProgressBarLoading } from './coreActions';

import awsHeader from '../helpers/awsAuth';
import { promiseSerial } from '../helpers/promises';
import { getSortOrder } from '../helpers/collections';
import k2aBaseApi from '../apis/key2act';

const BulkDevicesUrl = template.parse('/basagents/{agentId}/bulk-basdevices');
const DevicesUrl = template.parse('/basagents/{agentId}/basdevices{?configurationId,searchFilter,limit,page,sort}');
const DeviceUrl = template.parse('/basdevices/{basDeviceId}');
const DeviceObjectsUrl = template.parse('/basdevices/{basDeviceId}/basdevice-objects{?page,limit,sort}');
const ImportUrl = template.parse('/basdevices/{basDeviceId}/upload');
const WorkerProcessUrl = template.parse('/worker-process{?processId}');

const DEFAULT_DEVICES_LIMIT = 100;
const DEFAULT_SORT = 'name^asc';

export const createBulkDevice = (agentId, configurationId, devices) => async (dispatch, getState) => {
    const header = await awsHeader();

    const selectedAgentId = getState().basAgent.selectedAgentId;
    const agents = getState().basAgent.basAgents;
    const agentsInfo = Object.values(agents);

    const selectedAgent = agentsInfo.filter(agent => agent.id === selectedAgentId);
    const basType = selectedAgent[0].driverConfiguration[0].basType;

    const body = {
        devices: []
    };

    if (basType.toLowerCase() === 'niagara') {
        devices.forEach(device => {
            body.devices.push({
                basProfileId:    get(device, 'profileId', ''),
                configurationId: configurationId,
                name:            get(device, 'name', ''),
                oBIXPath:        get(device, 'oBIXPath', ''),
                type:            get(device, 'type', '')
            });
        });
    } else {
        const selectedDevices = getState().device.selectedDevices;
        selectedDevices.forEach(device => {
            body.devices.push({
                actualDeviceInstance: get(device, 'ActualDeviceInstance', -1),
                basProfileId:         get(device, 'profileId', ''),
                configurationId:      configurationId,
                deviceId:             get(device, 'DeviceId', ''),
                macAddress:           get(device, 'MAC', ''),
                model:                get(device, 'ModelName', ''),
                name:                 get(device, 'DeviceName', ''),
                virtualized:          get(device, 'Virtualized', false),
                virtualObjectId:      get(device, 'VirtualObjectID', 0)
            });
        });
    }

    try {
        const response = await k2aBaseApi.post(
            BulkDevicesUrl.expand({
                agentId
            }),
            body,
            header
        );

        const payload = get(response, 'data', []);

        dispatch(setTotalDevices(body.devices.length));
        dispatch(setProcessIds(payload));

        const deviceLoadProgressBarSettings = {
            currentValue: 0,
            label:        'Devices Created',
            maxValue:     body.devices.length
        };

        dispatch(setLoadProgress(deviceLoadProgressBarSettings));
        dispatch(toggleLoadProgressBar(true));

        await dispatch(collectTargetDevices(deviceLoadProgressBarSettings));

        const currentValue = getState().ui.settings.currentValue;

        dispatch(enqueueSnackbar(`${currentValue} device(s) processed successfully.`, 'success'));

    } catch (error) {
        dispatch(enqueueSnackbar('Device creation process failed.', 'error'));
    } finally {
        dispatch(toggleLoadProgressBar(false));
        dispatch(resetLoadProgress());
    }
};

export const collectTargetDevices = (deviceLoadProgressBarSettings, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    const processes = getState().device.processIds;

    try {
        const getPayload = async processes => {
            const ids = processes.map(process => process.processId);

            const randomId = ids[Math.floor(Math.random() * ids.length)];

            const results = await k2aBaseApi.get(
                WorkerProcessUrl.expand({
                    processId: randomId
                }),
                header
            );

            const payload = get(results, 'data', {});

            const recurseProcesses = async () => {
                return await getPayload(processes);
            };

            const timeout = ms => {
                return new Promise(resolve => setTimeout(resolve, ms));
            };

            if (isNil(payload.result)) {
                await timeout(Math.floor(Math.random() * 10000));

                await recurseProcesses();
            } else {
                const devices = [...get(payload, 'result.DeviceMutationResults', [])];

                deviceLoadProgressBarSettings.currentValue = devices.length;
                dispatch(setLoadProgress(deviceLoadProgressBarSettings));

                const modifiedProcesses = processes.filter(process => process.processId !== randomId);

                const recurseModifiedProcesses = async () => {
                    return await getPayload(modifiedProcesses);
                };

                if (modifiedProcesses.length) {
                    setTimeout(recurseModifiedProcesses, Math.floor(Math.random() * 10000));
                }
            }
        };

        await getPayload(processes);
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const createDevice = (agentId, deviceParams) => async (dispatch) => {
    const header = await awsHeader();
    try {
        const response = await k2aBaseApi.post(
            DevicesUrl.expand({
                agentId
            }),
            deviceParams,
            header
        );

        const payload = get(response, 'data', []);

        dispatch({
            type: DEVICE_ADD,
            payload
        });

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const fetchDevice = (basDeviceId) => async (dispatch, getState) => {
    const header = await awsHeader();
    try {
        const response = await k2aBaseApi.get(
            DeviceUrl.expand({
                basDeviceId
            }),
            header
        );
        const device = get(response, 'data');
        batch(() => {
            dispatch({
                type:    DEVICE_UPDATE,
                payload: {
                    id:    basDeviceId,
                    patch: device
                }
            });
            dispatch(setDevice(device));
        });
        return device;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchDevices = (companyId, agentId, configurationId, basType, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(resetDevices());

    try {
        let devices = [];

        dispatch(setDevicesLoading(true));

        const getDevices = async (page = 1) => {
            options.limit = DEFAULT_DEVICES_LIMIT;
            options.sort = DEFAULT_SORT;
            options.page = page;

            const response = await k2aBaseApi.get(
                DevicesUrl.expand({
                    agentId,
                    configurationId,
                    ...options
                }),
                header
            );

            const payload = get(response, 'data.data', []);
            const totalCount = get(response, 'data.meta.totalCount', 0);

            devices = [...devices, ...payload];

            page++;

            if (payload.length > 0 && devices.length < totalCount) {
                return await getDevices(page);
            }

            return;
        };

        await getDevices();

        dispatch({
            type:    FETCH_DEVICES,
            payload: devices
        });

        dispatch(setDevicesLoading(false));
        dispatch(setProgressBarLoading(false));

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchDevicesList = (agentId, configurationId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    let totalPayload = [];

    try {
        const getPayload = async (page = 1) => {
            options.limit = DEFAULT_DEVICES_LIMIT;
            options.sort = DEFAULT_SORT;
            options.page = page;

            const response = await k2aBaseApi.get(
                DevicesUrl.expand({
                    agentId,
                    configurationId,
                    ...options
                }),
                header
            );

            const payload = get(response, 'data.data', []);
            const totalCount = get(response, 'data.meta.totalCount', 0);

            totalPayload = [...totalPayload, ...payload];

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

        dispatch({
            type:    FETCH_DEVICES,
            payload: totalPayload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchDeviceObjects = (basDeviceId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    let totalPayload = [];

    try {
        const getPayload = async (page = 1) => {
            options.limit = DEFAULT_DEVICES_LIMIT;
            options.sort = DEFAULT_SORT;
            options.page = page;

            const response = await k2aBaseApi.get(
                DeviceObjectsUrl.expand({
                    basDeviceId,
                    ...options
                }),
                header
            );

            const payload = get(response, 'data.data', []);
            const totalCount = get(response, 'data.meta.totalCount', 0);

            totalPayload = [...totalPayload, ...payload];

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

        dispatch({
            type:    FETCH_DEVICE_OBJECTS,
            payload: totalPayload
        });

        return totalPayload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const reloadDeviceObjects = (basDeviceId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_DEVICES_LIMIT);

    const devices = getState().device.devices;

    try {
        const deviceResponse = await k2aBaseApi.get(
            DeviceObjectsUrl.expand({
                basDeviceId,
                ...options
            }),
            header
        );

        const payload = get(deviceResponse.data, 'data', []);

        devices.forEach(device => {
            if (device.id === basDeviceId) {
                device.objects = [...payload];
                device.loadFailed = false;
            }
        });

        dispatch({
            type:    FETCH_DEVICES,
            payload: devices
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const reorderDevices = newOrderBy => async (dispatch, getState) => {
    const currentState = getState().device;
    const order = getSortOrder(newOrderBy, currentState);
    dispatch({
        type:    DEVICE_REORDER,
        payload: {
            order,
            orderBy: newOrderBy
        }
    });
};

export const resetDevices = () => async (dispatch) => {
    dispatch({
        type:    FETCH_DEVICES,
        payload: []
    });
};

export const updateDeviceProfile = (companyId, agent, devices, basProfileId) => async dispatch => {
    const header = await awsHeader();

    const agentId = get(agent, 'id', '');
    const configurationId = get(agent, 'driverConfiguration[0].id', '');
    const basType = get(agent, 'driverConfiguration[0].basType', '').toLowerCase();

    const body = {
        name: '',
        basProfileId,
        configurationId
    };

    try {
        const tasks = devices.map(device => async () => {
            if (basType === 'niagara') {
                body.oBIXPath = device.oBIXPath;
                body.type = device.type;
            } else if (basType === 'bacnetip' || basType === 'bacnetethernet') {
                body.deviceId = get(device, 'deviceId', '');
                body.vendor = get(device, 'vendor', '');
                body.model = get(device, 'model', '');
                body.macAddress = get(device, 'macAddress', '');
            }

            body.name = get(device, 'name', '');

            await k2aBaseApi.put(
                DeviceUrl.expand({
                    basDeviceId: get(device, 'id', '')
                }),
                body,
                header
            );
        });

        await promiseSerial(tasks);

        dispatch(enqueueSnackbar('Profile updated successfully', 'success'));
        dispatch(fetchDevices(companyId, agentId, configurationId, basType));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteDevices = (companyId, agent, devices) => async dispatch => {
    const { headers } = await awsHeader();
    const agentId = agent.id;
    const configurationId = agent.driverConfiguration[0].id;
    const basType = agent.driverConfiguration[0].basType;

    try {
        const tasks = devices.map(device => async () => {
            await k2aBaseApi.delete(
                DeviceUrl.expand({
                    basDeviceId: device.id
                }),
                {
                    data: {
                        name: device.name
                    },
                    headers
                }
            );
            dispatch(removeDevice(device));
        });

        await promiseSerial(tasks);

        dispatch(enqueueSnackbar('Devices(s) deleted successfully', 'success'));
        dispatch(fetchDevices(companyId, agentId, configurationId, basType));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const manageDevice = (basDeviceId, deviceData, connectionData = {}) => async dispatch => {
    const header = await awsHeader();
    const { companyId, agentId, configurationId, basType } = connectionData;

    try {
        await k2aBaseApi.put(
            DeviceUrl.expand({
                basDeviceId
            }),
            deviceData,
            header
        );

        dispatch(enqueueSnackbar('Device updated successfully.', 'success'));
        dispatch(fetchDevices(companyId, agentId, configurationId, basType));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const importFile = (basDeviceId, file) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(
            ImportUrl.expand({
                basDeviceId
            }),
            file,
            header
        );

        dispatch(enqueueSnackbar('File imported successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const removeDevice = ({ id }) => ({
    type:    DEVICE_REMOVE,
    payload: { id }
});

export const toggleSelectDevice = id => ({
    type:    DEVICE_TOGGLE_SELECT,
    payload: { id }
});

export const updateDevice = (id, patch) => ({
    type:    DEVICE_UPDATE,
    payload: {
        id,
        patch
    }
});

export const setDevice = device => ({
    type:    SET_DEVICE,
    payload: device
});

export const setDevicesLoading = payload => ({
    type: SET_DEVICES_LOADING,
    payload
});

export const setDeviceObjects = deviceObjects => ({
    type:    FETCH_DEVICE_OBJECTS,
    payload: deviceObjects
});

export const devicesToggle = payload => ({
    type: DEVICES_TOGGLE,
    payload
});

export const setProcessIds = payload => ({
    type: SET_DEVICE_PROCESS_IDS,
    payload
});

export const setTotalDevices = payload => ({
    type: SET_DEVICE_TOTAL,
    payload
});
